import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthUser, Locale, Role } from '@cgp/shared'

type AuthState = {
  accessToken: string | null
  expiresAt: number | null
  user: AuthUser | null
  setSession: (payload: { accessToken: string; expiresIn: number; user: AuthUser }) => void
  setLocale: (locale: Locale) => void
  clear: () => void
  hasRole: (...roles: Role[]) => boolean
}

/**
 * The access token lives in memory + sessionStorage only; the refresh token is an
 * httpOnly cookie handled by the browser and never readable from JS.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      expiresAt: null,
      user: null,
      setSession: ({ accessToken, expiresIn, user }) =>
        set({ accessToken, expiresAt: Date.now() + expiresIn * 1000, user }),
      setLocale: (locale) => {
        const user = get().user
        if (user) set({ user: { ...user, preferredLocale: locale } })
      },
      clear: () => set({ accessToken: null, expiresAt: null, user: null }),
      hasRole: (...roles) => {
        const current = get().user?.roles ?? []
        return roles.some((role) => current.includes(role))
      },
    }),
    {
      name: 'cgp.session',
      storage: {
        getItem: (name) => {
          const value = sessionStorage.getItem(name)
          return value ? JSON.parse(value) : null
        },
        setItem: (name, value) => sessionStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => sessionStorage.removeItem(name),
      },
    },
  ),
)
